import React, { useCallback, useContext, useEffect, useMemo } from 'react';

// Utils
import { setCalendarFilterLocalStorage } from '@/utils/localStorage/scheduler/calendarFiltersLocalStorage';
import { setCalendarMediaLibraryLocalStorage } from '@/utils/localStorage/scheduler/calendarMediaLibraryLocalStorage';
import { setCalendarViewLocalStorage } from '@/utils/localStorage/scheduler/calendarViewLocalStorage';

const CalendarContext = React.createContext();

export const TOGGLE_CALENDAR_MOUNT = 'TOGGLE_CALENDAR_MOUNT';
export const UPDATE_CALENDAR_EVENT_ACTIVE = 'UPDATE_CALENDAR_EVENT_ACTIVE';
export const UPDATE_CALENDAR_FILTER_POST_STATUS = 'UPDATE_CALENDAR_FILTER_POST_STATUS';
export const UPDATE_CALENDAR_MEDIA_LIBRARY = 'UPDATE_CALENDAR_MEDIA_LIBRARY';
export const UPDATE_CALENDAR_TITLE_DATE = 'UPDATE_CALENDAR_TITLE_DATE';
export const UPDATE_CALENDAR_VIEW = 'UPDATE_CALENDAR_VIEW';

const DatepickerReducer = (state, action) => {
    switch (action.type) {
        case UPDATE_CALENDAR_TITLE_DATE:
            return { ...state, title: action.payload };
        case UPDATE_CALENDAR_VIEW:
            setCalendarViewLocalStorage(action.payload);
            return { ...state, view: action.payload };
        case UPDATE_CALENDAR_FILTER_POST_STATUS:
            return { ...state, filter_post_status: action.payload };
        case UPDATE_CALENDAR_EVENT_ACTIVE:
            setCalendarFilterLocalStorage(
                action.payload.cookie,
                !state.events[action.payload.event].active
            );
            return {
                ...state,
                events: {
                    ...state.events,
                    [action.payload.event]: {
                        ...state.events[action.payload.event],
                        active: !state.events[action.payload.event].active
                    }
                }
            };
        case UPDATE_CALENDAR_MEDIA_LIBRARY:
            setCalendarMediaLibraryLocalStorage(action.payload);
            return { ...state, openMediaLibrary: { active: action.payload } };
        /**
         * Workaround for @fullcalendar/react bug where changing views can sometimes fail
         * and throw errors. This implementation gracefully handles the failure by forcing
         * a calendar refresh when the direct view change fails.
         *
         * TODO: Remove this once the bug is fixed
         */
        case TOGGLE_CALENDAR_MOUNT: {
            const newState = { ...state, isCalendarRemounting: !state.isCalendarRemounting };

            if (action.payload) {
                newState.view = action.payload;
                setCalendarViewLocalStorage(action.payload);
            }

            return newState;
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
};

const CalendarProvider = ({ defaultValues, ...restProps }) => {
    const [state, dispatch] = React.useReducer(DatepickerReducer, {
        ...defaultValues
    });
    const dispatchCalendar = useCallback((...args) => dispatch(...args), [dispatch]);
    const calendarData = useMemo(() => state, [state]);

    useEffect(() => {
        if (calendarData?.isCalendarRemounting) {
            dispatchCalendar({ type: TOGGLE_CALENDAR_MOUNT });
        }
    }, [calendarData?.isCalendarRemounting]);

    return <CalendarContext.Provider value={{ calendarData, dispatchCalendar }} {...restProps} />;
};

const useCalendar = () => {
    const context = useContext(CalendarContext);
    if (!context) {
        throw new Error('useCalendar must be used within a CalendarProvider');
    }
    return context;
};

export { CalendarProvider, useCalendar };
